import { Alert, Box, Button, Card, Chip, CircularProgress, Container, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip as MuiTooltip, Typography } from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { useAuth } from "../hooks/useAuth";
import { CustomStyles } from "../utils/Styled";

const palette = {
    pageBg: "#edf2fa",
    panelBg: "#f9fbff",
    headerBlue: "#0d3a74",
    headerBlueDark: "#072a57",
    textDark: "#13233a",
    muted: "#5f6b7a",
    tableRowOdd: "#f2f7ff",
    tableRowEven: "#e8f1ff",
    tableBorder: "#c7d8f2",
};

const kpiPalette = [
    { bg: "#0d3a74", accent: "#2d67b2" },
    { bg: "#0b6b77", accent: "#2393a1" },
    { bg: "#d98a2c", accent: "#f3b04e" },
    { bg: "#a92f3f", accent: "#d45667" },
    { bg: "#1e7d44", accent: "#4db06f" },
];

const pipelinePalette = ["#3a74bf", "#2f65aa", "#24528a", "#1f3f6e"];

const formatLastSynced = (value) => {
    if (!value) return null;

    const parsed = moment(value);
    if (!parsed.isValid()) return null;

    return parsed.subtract(8, "hours").format("DD/MM/YYYY hh:mm A");
};

const liveSyncChipLabel = (lastSyncedAt) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "success.main", flexShrink: 0 }} />
        <span>{`Live sync from Million Cloud Database · 15 minute cache @Last synced: ${lastSyncedAt}`}</span>
    </Box>
);

const initialDashboardData = {
    customer: {
        name: "",
        industry: "",
        owner: "",
        remark1: "",
        remark2: "",
        remark3: "",
        remark4: "",
        remark5: "",
        salesAgent: "",
        sinceYear: "",
        ytdSales: 0,
        invoices: 0,
        invoiceAmount: 0,
        openQuotation: 0,
        openQuotationAmount: 0,
    },
    pipeline: [],
    productBreakdown: [],
    monthlyQuoteInvoiceStatus: [],
    yearlyMonthlySales: [],
};

const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
];

const monthAbbrevToValue = {
    JAN: 1,
    FEB: 2,
    MAR: 3,
    APR: 4,
    MAY: 5,
    JUN: 6,
    JUL: 7,
    AUG: 8,
    SEP: 9,
    OCT: 10,
    NOV: 11,
    DEC: 12,
};

const moneyCompact = (value) => {
    const amount = Number(value);
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const tableHeaderCellSx = {
    backgroundColor: "#0d3a74",
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 700,
    textAlign: "center",
    borderColor: "#335f96",
    py: { xs: 1, sm: 1.25 },
};

const tableBodyCellSx = {
    color: "#13345f",
    fontSize: 16,
    py: { xs: 1, sm: 1.25 },
    borderColor: "#d7e5fb",
};

const CustomerKpiCard = ({ title, value, bg, accent }) => {
    return (
        <Card
            elevation={0}
            sx={{
                background: `linear-gradient(145deg, ${bg}, ${accent})`,
                color: "#ffffff",
                borderRadius: 1,
                textAlign: "center",
                py: 2,
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)",
            }}
        >
            <Typography variant="h5" fontWeight={500} lineHeight={1.15}>
                {value}
            </Typography>
            <Typography variant="subtitle1" lineHeight={1.2}>
                {title}
            </Typography>
        </Card>
    );
};

const CustomerRemarkCard = ({ title, value }) => {
    return (
        <Card
            elevation={0}
            sx={{
                backgroundColor: palette.panelBg,
                border: "1px solid #d5deea",
                borderRadius: 1,
                p: 1.25,
                minHeight: 78,
            }}
        >
            <Typography variant="subtitle2" sx={{ color: palette.muted, fontWeight: 700 }}>
                {title}
            </Typography>
            <Typography variant="body2" sx={{ color: palette.textDark, mt: 0.25, wordBreak: "break-word" }}>
                {value || "-"}
            </Typography>
        </Card>
    );
};

const PerfectTrapeziumPipeline = ({ data }) => {
    const viewBoxWidth = 1000;
    const viewBoxHeight = 560;

    // Keep a straight-sided funnel regardless of stage count.
    const stageCount = Math.max(data.length, 1);
    const topWidth = 1.0;
    const bottomWidth = 0.34;
    const boundaryWidths = Array.from(
        { length: stageCount + 1 },
        (_, index) => topWidth - ((topWidth - bottomWidth) * index) / stageCount
    );
    const boundaryY = Array.from({ length: stageCount + 1 }, (_, index) => index / stageCount);

    const pointsByStage = data.map((item, index) => {
        const topY = boundaryY[index] * viewBoxHeight;
        const bottomY = boundaryY[index + 1] * viewBoxHeight;

        const topHalf = (boundaryWidths[index] * viewBoxWidth) / 2;
        const bottomHalf = (boundaryWidths[index + 1] * viewBoxWidth) / 2;
        const centerX = viewBoxWidth / 2;

        const polygonPoints = [
            `${centerX - topHalf},${topY}`,
            `${centerX + topHalf},${topY}`,
            `${centerX + bottomHalf},${bottomY}`,
            `${centerX - bottomHalf},${bottomY}`,
        ].join(" ");

        return {
            ...item,
            polygonPoints,
            labelX: centerX,
            labelY: (topY + bottomY) / 2,
        };
    });

    const outlinePoints = [
        `${viewBoxWidth / 2 - (boundaryWidths[0] * viewBoxWidth) / 2},0`,
        `${viewBoxWidth / 2 + (boundaryWidths[0] * viewBoxWidth) / 2},0`,
        `${viewBoxWidth / 2 + (boundaryWidths[boundaryWidths.length - 1] * viewBoxWidth) / 2},${viewBoxHeight}`,
        `${viewBoxWidth / 2 - (boundaryWidths[boundaryWidths.length - 1] * viewBoxWidth) / 2},${viewBoxHeight}`,
    ].join(" ");

    return (
        <svg viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
            {pointsByStage.map((segment, index) => (
                <g key={segment.name}>
                    <polygon points={segment.polygonPoints} fill={segment.fill} />
                    {index > 0 && (
                        <line
                            x1={viewBoxWidth / 2 - (boundaryWidths[index] * viewBoxWidth) / 2}
                            y1={boundaryY[index] * viewBoxHeight}
                            x2={viewBoxWidth / 2 + (boundaryWidths[index] * viewBoxWidth) / 2}
                            y2={boundaryY[index] * viewBoxHeight}
                            stroke="#2a4f81"
                            strokeWidth="2"
                        />
                    )}
                    <text
                        x={segment.labelX}
                        y={segment.labelY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#ffffff"
                        fontWeight="600"
                        fontSize="24"
                    >
                        {`${segment.name}: ${moneyCompact(segment.amount)}`}
                    </text>
                </g>
            ))}
            <polygon points={outlinePoints} fill="none" stroke="#2a4f81" strokeWidth="2" />
        </svg>
    );
};

export const CustomerDashboardPage = () => {
    const classes = CustomStyles;
    const navigate = useNavigate();
    const { orgId } = useParams();
    const { logout } = useAuth();
    const currentYear = new Date().getFullYear();
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [firstTransactionYear, setFirstTransactionYear] = useState(null);
    const [lastSyncedAt, setLastSyncedAt] = useState(null);
    const [dashboardData, setDashboardData] = useState(initialDashboardData);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState("");

    const getValidFirstTransactionYear = (value) => {
        const parsedYear = Number(value);
        return Number.isInteger(parsedYear) && parsedYear > 0 && parsedYear <= currentYear
            ? parsedYear
            : null;
    };

    useEffect(() => {
        if (!orgId) return;

        let isMounted = true;

        const fetchDashboardData = async () => {
            setIsLoading(true);
            setFetchError("");

            try {
                const { data } = await axios.get(
                    process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${orgId}`,
                    {
                        params: {
                            month: selectedMonth,
                            year: selectedYear,
                        },
                    }
                );

                if (!isMounted) return;

                const payload = data?.data || {};
                const validFirstTransactionYear = getValidFirstTransactionYear(payload.firstTransactionYear);
                setLastSyncedAt(formatLastSynced(payload.latestTransactionUpdatedDate));
                setDashboardData({
                    customer: {
                        ...initialDashboardData.customer,
                        ...(payload.customer || {}),
                    },
                    pipeline: Array.isArray(payload.pipeline) ? payload.pipeline : [],
                    productBreakdown: Array.isArray(payload.productBreakdown) ? payload.productBreakdown : [],
                    monthlyQuoteInvoiceStatus: Array.isArray(payload.monthlyQuoteInvoiceStatus) ? payload.monthlyQuoteInvoiceStatus : [],
                    yearlyMonthlySales: Array.isArray(payload.yearlyMonthlySales) ? payload.yearlyMonthlySales : [],
                });
                setFirstTransactionYear(
                    validFirstTransactionYear
                );
            } catch (error) {
                if (!isMounted) return;

                if (error.response?.status === 401) {
                    logout(error.response?.data?.message || "Your session has expired.");
                    return;
                }

                // For missing customers, keep the year filter constrained to the current year.
                setFirstTransactionYear(currentYear);
                setLastSyncedAt(null);
                setFetchError(error.response?.data?.message || "Failed to load customer dashboard data.");
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchDashboardData();

        return () => {
            isMounted = false;
        };
    }, [logout, orgId, selectedMonth, selectedYear]);

    const kpiCards = useMemo(
        () => [
            { title: "- YTD Sales -", value: moneyCompact(dashboardData.customer.ytdSales) },
            { title: "- Invoice Count -", value: dashboardData.customer.invoices },
            { title: "- Invoice Amount -", value: moneyCompact(dashboardData.customer.invoiceAmount) },
            { title: "- Open Quotation Count -", value: dashboardData.customer.openQuotation },
            { title: "- Open Quotation Amount -", value: moneyCompact(dashboardData.customer.openQuotationAmount) },
        ],
        [dashboardData.customer]
    );

    const remarkCards = useMemo(
        () => [
            { title: "Industry", value: dashboardData.customer.industry },
            { title: "Sales Agent", value: dashboardData.customer.salesAgent },
            { title: "Remark 1", value: dashboardData.customer.remark1 },
            { title: "Remark 2", value: dashboardData.customer.remark2 },
            { title: "Remark 3", value: dashboardData.customer.remark3 },
        ],
        [dashboardData.customer]
    );

    const funnelData = useMemo(
        () =>
            dashboardData.pipeline.map((item, index) => ({
                name: item.stage,
                amount: item.amount,
                fill: pipelinePalette[index] || pipelinePalette[pipelinePalette.length - 1],
            })),
        [dashboardData.pipeline]
    );

    const yearlySalesTableRows = useMemo(() => {
        const midpoint = Math.ceil(dashboardData.yearlyMonthlySales.length / 2);
        const left = dashboardData.yearlyMonthlySales.slice(0, midpoint);
        const right = dashboardData.yearlyMonthlySales.slice(midpoint);
        return left.map((item, index) => ({
            left: item,
            right: right[index] || { month: "", amount: "" },
        }));
    }, [dashboardData.yearlyMonthlySales]);

    const yearlySalesTotal = useMemo(
        () => dashboardData.yearlyMonthlySales.reduce((total, item) => total + Number(item.amount || 0), 0),
        [dashboardData.yearlyMonthlySales]
    );

    const yearOptions = useMemo(() => {
        const fallbackStartYear = currentYear - 2;
        const startYear = Number.isInteger(firstTransactionYear)
            ? Math.min(firstTransactionYear, currentYear)
            : fallbackStartYear;

        return Array.from(
            { length: currentYear - startYear + 1 },
            (_, index) => currentYear - index
        );
    }, [currentYear, firstTransactionYear]);

    useEffect(() => {
        if (yearOptions.length > 0 && !yearOptions.includes(selectedYear)) {
            setSelectedYear(yearOptions[0]);
        }
    }, [selectedYear, yearOptions]);

    const getMonthYearFromPeriod = (period) => {
        const periodText = String(period || "").trim().toUpperCase();
        const [monthPart, yearPart] = periodText.split(/\s+/);
        const parsedMonth = monthAbbrevToValue[monthPart] || selectedMonth;
        const parsedYear = Number(yearPart);

        return {
            month: parsedMonth,
            year: Number.isFinite(parsedYear) ? parsedYear : selectedYear,
        };
    };

    const openMonthlyProductDetails = (period) => {
        if (!orgId) return;

        const { month, year } = getMonthYearFromPeriod(period);

        const searchParams = new URLSearchParams({
            orgId: String(orgId),
            from_month: String(month),
            from_year: String(year),
            to_month: String(month),
            to_year: String(year),
        });

        window.open(`/customer-dashboard/monthly-product-details?${searchParams.toString()}`, "_blank", "noopener,noreferrer");
    };

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }}>
                <Paper elevation={0} sx={{ backgroundColor: palette.pageBg, border: "1px solid #d5deea", borderRadius: 1, p: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                            <Typography variant="h5" color={palette.headerBlue} fontWeight="bold">
                                Customer Summary Dashboard
                            </Typography>
                            {lastSyncedAt && <Chip size="small" label={liveSyncChipLabel(lastSyncedAt)} />}
                        </Box>
                        <Button variant="outlined" sx={{ color: palette.headerBlue, borderColor: "#7f9dc4" }} onClick={() => navigate("/organizations")}>
                            Back
                        </Button>
                    </Stack>

                    {fetchError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {fetchError}
                        </Alert>
                    )}

                    {isLoading && (
                        <Alert
                            severity="info"
                            icon={<CircularProgress size={16} color="inherit" />}
                            sx={{ mb: 1.5, color: palette.headerBlue, backgroundColor: "#eaf2ff", border: "1px solid #c7d8f2" }}
                        >
                            Loading dashboard data...
                        </Alert>
                    )}

                    <Box
                        sx={{
                            background: `linear-gradient(135deg, ${palette.headerBlue}, ${palette.headerBlueDark})`,
                            borderRadius: 1.5,
                            px: 3,
                            py: 2,
                            mb: 2.5,
                        }}
                    >
                        <Typography variant="h5" color="#fff" fontWeight={500} lineHeight={1.2}>
                            {dashboardData.customer.name || "-"} | Owner: {dashboardData.customer.owner || "-"}
                        </Typography>
                        <Typography variant="h6" color="#dbe8ff" fontWeight={500} lineHeight={1.2} sx={{ mt: 0.75 }}>
                            Since Year: {dashboardData.customer.sinceYear || "-"}
                        </Typography>
                    </Box>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        justifyContent="flex-end"
                        alignItems={{ xs: "stretch", sm: "center" }}
                        sx={{ mb: 2.25 }}
                    >
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: { xs: "100%", sm: 170 },
                                backgroundColor: palette.panelBg,
                                borderRadius: 1,
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#7f9dc4" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: palette.headerBlue },
                                "& .MuiInputLabel-root": { color: palette.muted },
                                "& .MuiSelect-select": { color: palette.textDark, fontWeight: 600 },
                            }}
                        >
                            <InputLabel id="customer-dashboard-month-label">Month</InputLabel>
                            <Select
                                labelId="customer-dashboard-month-label"
                                id="customer-dashboard-month"
                                value={selectedMonth}
                                label="Month"
                                disabled={isLoading}
                                onChange={(event) => setSelectedMonth(event.target.value)}
                            >
                                {monthOptions.map((month) => (
                                    <MenuItem key={month.value} value={month.value}>
                                        {month.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl
                            size="small"
                            sx={{
                                minWidth: { xs: "100%", sm: 130 },
                                backgroundColor: palette.panelBg,
                                borderRadius: 1,
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#7f9dc4" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: palette.headerBlue },
                                "& .MuiInputLabel-root": { color: palette.muted },
                                "& .MuiSelect-select": { color: palette.textDark, fontWeight: 600 },
                            }}
                        >
                            <InputLabel id="customer-dashboard-year-label">Year</InputLabel>
                            <Select
                                labelId="customer-dashboard-year-label"
                                id="customer-dashboard-year"
                                value={selectedYear}
                                label="Year"
                                disabled={isLoading}
                                onChange={(event) => setSelectedYear(event.target.value)}
                            >
                                {yearOptions.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Box
                        sx={{
                            display: "grid",
                            gap: 2,
                            mb: 2.5,
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                md: "repeat(3, minmax(0, 1fr))",
                                lg: "repeat(5, minmax(0, 1fr))",
                            },
                        }}
                    >
                        {kpiCards.map((card, index) => (
                            <CustomerKpiCard
                                key={card.title}
                                title={card.title}
                                value={card.value}
                                bg={kpiPalette[index % kpiPalette.length].bg}
                                accent={kpiPalette[index % kpiPalette.length].accent}
                            />
                        ))}
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gap: 1.25,
                            mb: 2.5,
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "repeat(5, minmax(0, 1fr))",
                            },
                        }}
                    >
                        {remarkCards.map((card) => (
                            <CustomerRemarkCard key={card.title} title={card.title} value={card.value} />
                        ))}
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" color={palette.headerBlue} mb={1}>
                                Quotation Pipeline (YTD)
                            </Typography>
                            <Paper elevation={0} sx={{ backgroundColor: palette.panelBg, border: "1px solid #d5deea", borderRadius: 1, height: 380, p: 1 }}>
                                <PerfectTrapeziumPipeline data={funnelData} />
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" color={palette.headerBlue} mb={1} textAlign={{ xs: "left", md: "center" }}>
                                Product Breakdown (YTD Qty)
                            </Typography>
                            <Paper elevation={0} sx={{ backgroundColor: palette.panelBg, border: "1px solid #d5deea", borderRadius: 1, height: 380, p: 1 }}>
                                <Box sx={{ display: "flex", height: "100%", alignItems: "center", gap: 1 }}>
                                    <Box sx={{ width: { xs: "56%", sm: "58%" }, minWidth: 220, height: "100%" }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={dashboardData.productBreakdown}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={95}
                                                    innerRadius={42}
                                                    isAnimationActive
                                                >
                                                    {dashboardData.productBreakdown.map((entry) => (
                                                        <Cell key={entry.name} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                                        <Stack spacing={1}>
                                            {dashboardData.productBreakdown.map((entry) => (
                                                <Box key={entry.name} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                    <Box sx={{ width: 12, height: 12, borderRadius: "2px", backgroundColor: entry.color, flexShrink: 0 }} />
                                                    <MuiTooltip title={entry.name} arrow placement="top-start">
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: palette.textDark,
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                maxWidth: "100%",
                                                                cursor: "default",
                                                            }}
                                                        >
                                                            {entry.name}
                                                        </Typography>
                                                    </MuiTooltip>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h5" color={palette.headerBlue} mb={1.25}>
                            Monthly Quote & Invoice Status
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${palette.tableBorder}`, borderRadius: 1, backgroundColor: palette.panelBg }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={tableHeaderCellSx}>Quote Period</TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Quote Amount</TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Quote Number</TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Quote Product Number</TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Invoice Amount</TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Invoice Number</TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Invoice Product Number</TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Product Details</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dashboardData.monthlyQuoteInvoiceStatus.map((row, index) => (
                                        <TableRow key={row.period} sx={{ backgroundColor: index % 2 === 0 ? palette.tableRowOdd : palette.tableRowEven }}>
                                            <TableCell sx={tableBodyCellSx}>{row.period}</TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{moneyCompact(row.quoteAmount)}</TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{row.quoteNumber}</TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{row.quoteProductNumber}</TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{moneyCompact(row.invoiceAmount)}</TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{row.invoiceNumber}</TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{row.invoiceProductNumber}</TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>
                                                <Button size="small" variant="outlined" onClick={() => openMonthlyProductDetails(row.period)}>
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h5" color={palette.headerBlue} mb={1.25}>
                            Yearly Monthly Sales Figure
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${palette.tableBorder}`, borderRadius: 1, backgroundColor: palette.panelBg }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={tableHeaderCellSx}>Monthly</TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Sales Amount</TableCell>
                                        <TableCell sx={{ ...tableHeaderCellSx, width: 44 }}></TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Monthly</TableCell>
                                        <TableCell sx={tableHeaderCellSx}>Sales Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {yearlySalesTableRows.map((row, index) => (
                                        <TableRow key={row.left.month} sx={{ backgroundColor: index % 2 === 0 ? palette.tableRowOdd : palette.tableRowEven }}>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{row.left.month}</TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{moneyCompact(row.left.amount)}</TableCell>
                                            <TableCell sx={{ borderLeft: "1px solid #d7e5fb", borderRight: "1px solid #d7e5fb", backgroundColor: "#e3edfc" }} />
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{row.right.month}</TableCell>
                                            <TableCell sx={{ ...tableBodyCellSx, textAlign: "center" }}>{row.right.month ? moneyCompact(row.right.amount) : ""}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ backgroundColor: "#d8e6fb" }}>
                                        <TableCell
                                            colSpan={4}
                                            sx={{
                                                ...tableBodyCellSx,
                                                textAlign: "right",
                                                fontWeight: 700,
                                                color: palette.headerBlue,
                                                borderRight: "1px solid #d7e5fb",
                                            }}
                                        >
                                            Total Sales Amount
                                        </TableCell>
                                        <TableCell sx={{ ...tableBodyCellSx, textAlign: "center", fontWeight: 700, color: palette.headerBlue }}>
                                            {moneyCompact(yearlySalesTotal)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Paper>
            </Container>
        </div>
    );
};
